/**
 * Iperf test settings shared by the Iperf tab (start a test now) and iperf
 * schedules (start the same test at a set site time). A schedule stores the
 * exact request body a regular test sends to POST /tree-mgmt/iperf-test.
 */

/**
 * Defaults of an iperf test started from the UI (Iperf tab and schedules):
 * child → parent (what Optimize uses) plus every device → CAP, 15 s, UDP
 * offered at 250 Mbps — near what a good link carries, so loss figures
 * mean something (offering far more than the link carries reports
 * saturation, not link quality).
 *
 * udp_bandwidth is in Mbps as the device firmware applies it: runs sent
 * with 2000 measured 118 Mbps, which neither kbps nor bits/s allows.
 */
export const DEFAULT_IPERF_SETTINGS = Object.freeze({
  duration: 15,
  protocol: 'UDP',
  // Auto: each UDP test offers a little more than the link under test
  // should carry (from its reported MCS / streams / width; slowest hop for
  // path tests). udpBandwidth is then the fallback for links with no
  // rate report.
  udpBandwidthAuto: true,
  udpBandwidth: 250,
  includeHop: true,
  includeE2E: false,
  includeAllToCap: true,
  reverseDirection: false,
  // CAP trees tested at the same time when several CAPs are selected.
  parallelTrees: 1,
});

/** Most CAP trees one test may run at the same time (backend limit). */
export const MAX_PARALLEL_TREES = 8;

/**
 * CAP part of the request. Every CAP of a multi-CAP site is sent as "all",
 * so the backend resolves the CAPs when the test starts (a schedule then
 * also covers CAPs added later).
 */
export function buildIperfCapPayload(selectedCapSerials, allCaps) {
  const serials = (selectedCapSerials || []).filter(Boolean);
  if (serials.length === 0) return null;
  if (serials.length === 1) return { cap_serial: serials[0] };
  if ((allCaps || []).length > 1 && serials.length === allCaps.length) return { cap_serial: 'all' };
  return { cap_serials: serials };
}

/** Request body fields for the test settings (no CAPs, no name). */
export function iperfSettingsToRequest(settings) {
  const s = { ...DEFAULT_IPERF_SETTINGS, ...(settings || {}) };
  return {
    duration: Number(s.duration) || DEFAULT_IPERF_SETTINGS.duration,
    protocol: s.protocol || 'UDP',
    udp_bandwidth: Number(s.udpBandwidth) || DEFAULT_IPERF_SETTINGS.udpBandwidth,
    udp_bandwidth_auto: Boolean(s.udpBandwidthAuto),
    include_hop: Boolean(s.includeHop),
    include_e2e: Boolean(s.includeE2E),
    include_all_to_cap: Boolean(s.includeAllToCap),
    reverse_direction: Boolean(s.reverseDirection),
    parallel_trees: Math.min(Math.max(Number(s.parallelTrees) || 1, 1), MAX_PARALLEL_TREES),
  };
}

/** Test settings from a stored request body (missing fields → defaults). */
export function iperfRequestToSettings(req) {
  const r = req || {};
  const flag = (v, def) => (typeof v === 'boolean' ? v : def);
  let { includeHop, includeE2E, includeAllToCap } = DEFAULT_IPERF_SETTINGS;
  const explicit = ['include_hop', 'include_e2e', 'include_all_to_cap'].some((k) => typeof r[k] === 'boolean');
  if (explicit) {
    includeHop = flag(r.include_hop, true);
    includeE2E = flag(r.include_e2e, true);
    includeAllToCap = flag(r.include_all_to_cap, true);
  } else if (r.test_mode) {
    const mode = String(r.test_mode).toLowerCase();
    includeHop = mode === 'both' || mode === 'hop_only';
    includeE2E = mode === 'both' || mode === 'e2e_only';
    includeAllToCap = false;
  }
  return {
    duration: Number(r.duration) > 0 ? Number(r.duration) : DEFAULT_IPERF_SETTINGS.duration,
    protocol: String(r.protocol || 'UDP').toUpperCase(),
    udpBandwidth: Number(r.udp_bandwidth) > 0 ? Number(r.udp_bandwidth) : DEFAULT_IPERF_SETTINGS.udpBandwidth,
    // Stored before auto existed (or explicitly off): fixed rate.
    udpBandwidthAuto: r.udp_bandwidth_auto === true,
    includeHop,
    includeE2E,
    includeAllToCap,
    reverseDirection: Boolean(r.reverse_direction),
    parallelTrees: Number(r.parallel_trees) > 0 ? Number(r.parallel_trees) : 1,
  };
}

/** CAP serials selected by a stored request ("all" → every CAP given). */
export function iperfRequestCapSerials(req, allCaps) {
  const r = req || {};
  if (Array.isArray(r.cap_serials) && r.cap_serials.length > 0) return r.cap_serials.filter(Boolean);
  if (String(r.cap_serial || '').toLowerCase() === 'all') return (allCaps || []).map((c) => c.serial_number);
  return r.cap_serial ? [r.cap_serial] : [];
}

/** True when the settings select no test type. */
export function iperfSettingsEmpty(settings) {
  return !settings?.includeHop && !settings?.includeE2E && !settings?.includeAllToCap;
}

/**
 * True when a run with these settings can anchor Optimize: it needs hop
 * (child → parent) tests; end-to-end is optional, all-to-CAP alone is not
 * enough.
 */
export function iperfSettingsAnchorOptimize(settings) {
  return Boolean(settings?.includeHop);
}

/** Short summary of what a stored request measures, e.g. "Hop + E2E · uplink · UDP 20s". */
export function describeIperfRequest(req) {
  const s = iperfRequestToSettings(req);
  const kinds = [s.includeHop && 'Hop', s.includeE2E && 'E2E', s.includeAllToCap && 'All-to-CAP'].filter(Boolean);
  let proto = s.protocol;
  if (s.protocol === 'UDP') proto = s.udpBandwidthAuto ? 'UDP auto rate' : `UDP ${s.udpBandwidth} Mbps`;
  const parallel = s.parallelTrees > 1 ? ` · ${s.parallelTrees} trees at a time` : '';
  return `${kinds.join(' + ') || 'Nothing'} · ${s.reverseDirection ? 'from CAP' : 'toward CAP'} · ${proto} · ${s.duration}s${parallel}`;
}

/** Offered-rate note for one completed UDP test, e.g. "offered 61 Mbps (link rate)". */
export function describeOfferedRate(test) {
  if (!test || !(Number(test.offered_mbps) > 0)) return '';
  const basis = { link_rate: 'link rate', fallback: 'no link rate, fallback', manual: 'fixed' }[test.offered_basis] || '';
  return `offered ${test.offered_mbps} Mbps${basis ? ` (${basis})` : ''}`;
}

/** CAP scope of a stored request, e.g. "All CAPs" or "3 CAPs". */
export function describeIperfCapScope(req) {
  const r = req || {};
  const focus = Array.isArray(r.focus_serials) && r.focus_serials.length > 0
    ? ` · ${r.focus_serials.length} selected devices`
    : '';
  if (String(r.cap_serial || '').toLowerCase() === 'all') return `All CAPs${focus}`;
  if (Array.isArray(r.cap_serials) && r.cap_serials.length > 0) {
    return `${r.cap_serials.length} CAP${r.cap_serials.length === 1 ? '' : 's'}${focus}`;
  }
  return r.cap_serial ? `CAP ${r.cap_serial}${focus}` : `No CAP${focus}`;
}

/**
 * Form state of a scheduled iperf test (CAPs + settings) from a stored
 * request; a missing request gives the defaults on "All CAPs".
 * "all" stays "all", so new CAPs are included when the test starts.
 */
export function iperfTestFormFromRequest(req, allCaps) {
  const r = req || {};
  const all = !req || String(r.cap_serial || '').toLowerCase() === 'all';
  return {
    allCaps: all,
    capSerials: all ? [] : iperfRequestCapSerials(r, allCaps),
    focusSerials: Array.isArray(r.focus_serials) ? r.focus_serials : [],
    testName: r.name || '',
    settings: req ? iperfRequestToSettings(r) : { ...DEFAULT_IPERF_SETTINGS },
  };
}

/** Stored request body from that form state. */
export function iperfTestFormToRequest(form) {
  const capPart = form.allCaps ? { cap_serial: 'all' } : buildIperfCapPayload(form.capSerials, []);
  const req = { ...(capPart || {}), ...iperfSettingsToRequest(form.settings) };
  if (form.testName && form.testName.trim()) req.name = form.testName.trim();
  if (form.focusSerials && form.focusSerials.length > 0) req.focus_serials = form.focusSerials;
  return req;
}

/** First problem with that form state, or ''. */
export function iperfTestFormProblem(form) {
  if (!form.allCaps && form.capSerials.length === 0) return 'Select the CAPs to test.';
  if (iperfSettingsEmpty(form.settings)) return 'Select at least one test type.';
  return '';
}
